import type { RelatedSite } from './types';

/**
 * 関連事業 詳細(JA)後半: Queen's Butler / 株式会社ファミリーロマンス / 石井裕一オフィシャルサイト。
 * 2026-09-18 二版: 全文をアカデミーの語り口で書き下ろし(公式本文・旧版本文からの 30 字以上の連続一致 0 を自己検査)。
 * 事実の出典(実読・ファイル:行):
 *  - Queen's Butler: site-queensbutler/src/content/pages/P-011.json:25-153(女王・住所・執事在籍・最大6名・食事なし・イチイ材),
 *    P-012.json:11-173(4サービスの比較・2〜6時間・3時間・受注生産・送迎5か所), P-013.json:22-96(鍵・無人不可・深夜1名・寝台=実習設備),
 *    P-060.json:30-170(屋上・ゆりかもめ/りんかい線・男女の執事), P-100.json:7-193(1組貸切・完全予約制・120インチ), P-200.json:43-108(11の空間・シャワールーム・マリアテレサ),
 *    P-300.json:128(衣装の目安サイズ), P-400.json(受注生産)。料金・送迎の料金・商品名は書かない。
 *  - ファミリーロマンス / 石井裕一: 当サイト 2026-07-05 版(git main:src/i18n/local/ja/related.ts:234-420)の事実のみ(2004創業・24歳・5,000名・NDA・
 *    Family Romance, LLC=ヘルツォーク監督・第72回カンヌ監督週間・ハーバード講演とガラス賞・『人間レンタル屋』鉄人社2019・1981年生)。新事実は足さない。
 */
const sitesB: RelatedSite[] = [
  // ═══════════════ Queen's Butler ═══════════════
  {
    slug: 'queens-butler',
    name: "Queen's Butler",
    role: '協会が運営する女性のための執事サロン',
    tagline:
      'お台場の館を舞台に、協会が立ち上げた新しいブランド。女性のためのサロン、撮影や会議に使えるスタジオ、モーニングコートを着て所作に触れる見習い体験、そして館の意匠を届けるショップ。四つの扉が一つの館につながっています。',
    hubImage: { src: '/images/related/queens-butler/s02-dining-afternoon-tea.jpg', alt: '三段スタンドと紅茶を正餐の間へ運ぶ執事（館の実写）' },
    heroImage: { src: '/images/related/queens-butler/bl02-building-night.jpg', alt: '夜、館の入るビルの前で車の扉を開けて迎える執事（Queen\'s Butler サイトの画像）', position: 'center bottom' },
    heroTitle: "Queen's Butler",
    heroTitleEn: 'A HOUSE WHERE THE GUEST IS THE QUEEN',
    heroLead:
      'アカデミーと同じ協会が手がける、お台場・青海の館。ここでは扉の内側に入った人が、その時間の主になります。教室で教えている執事の所作を、仕えられる側として味わえる場所として、学びの場の視点から紹介します。',
    officialUrl: 'https://queensbutler.jp/',
    officialLabel: "Queen's Butler の公式サイトを開く",
    lastUpdated: '最終更新: 2026年9月18日',
    blocks: [
      {
        kind: 'twocol',
        label: 'THE IDEA',
        title: '扉をくぐった人が、その時間の主になる',
        sub: 'Serving whoever walks in',
        body: [],
        subs: [
          { t: '女王に仕える執事、という芯', b: "Queen's Butler は協会が新しく立ち上げたブランドで、東京都江東区青海2-7-4、お台場のビルの中にあるアンティーク調の館を舞台にしています。ブランドの芯にあるのは「女王に仕える執事」という一語です。サロンに来た女性客も、撮影や会議で館を借りたクルーも、見習い体験に来た人も、執事にとっては等しく「その時間の館の主」であり、同じ礼で迎えられます。" },
          { t: '派手さより、先に読んで整える', b: 'この言葉は看板の飾りではなく、館の運営のすべてを決める原則だと Queen\'s Butler は説明しています。派手な演出を重ねるのではなく、主が次に何を必要とするかを先に読んで整える。入口から部屋まで案内し、必要な作法は隣でそっと伝え、知らないことを理由に急かさない。館の執事の動きは、この原則から逆算されています。' },
          { t: '男女の執事が必ず付く', b: '館には男性の執事も女性の執事も在籍しており、館内のどのサービスでも執事が必ず付きます。アカデミーの教室で「頼まれる前に整える」と教えていることを、お客様の側から体験できる場所——学びの場から見た Queen\'s Butler の位置づけは、そこにあります。' },
        ],
        image: { src: '/images/related/queens-butler/s10-door-welcome.jpg', alt: '館の扉で来客を迎える執事（Queen\'s Butler の実写）' },
      },
      {
        kind: 'cards',
        label: 'FOUR DOORS',
        title: '同じ館へ通じる、四つの扉',
        sub: 'One house, four ways in',
        lede:
          '四つのサービスの違いは、館を「どの扉から使うか」の違いにすぎません。仕えられたいならサロン、館を撮りたい・会議に使いたいならスタジオ、執事の装いを着てみたいなら見習い体験、館の意匠を持ち帰りたいならショップ。それぞれの扉の向こうに、同じ館と同じ執事が待っています。',
        cards: [
          {
            title: 'サロン——女性のための、仕えられる時間',
            desc: '女性の主が館を一組で貸し切り、執事に仕えられて過ごす時間です。予約制で、利用できるのは女性のみ。館は食事を出さない代わりに飲食物の持ち込みができ、最大六名まで一緒に過ごせます。',
            image: { src: '/images/related/queens-butler/f03-lounge-blanket.jpg', alt: '暖炉の前でひざ掛けを整える執事（館の実写）' },
          },
          {
            title: 'スタジオ——撮影・会議・控室・ロケ',
            desc: '館そのものを撮影の舞台に、会議の場に、控室に、あるいはロケ地として使い、執事が現場を支えます。背景を借りるだけではなく、扉での受付、調度の案内、給仕までが執事の仕事です。男女どちらでも利用できます。',
            image: { src: '/images/related/queens-butler/sc10-studio-shoot-wide.jpg', alt: '暖炉の間での撮影風景。ソファのモデルとカメラマン（館の実写）' },
          },
          {
            title: '見習い体験——モーニングコートで三時間',
            desc: 'モーニングコートを一式着て、一礼、立ち姿、銀器の扱いに三時間ほど触れる体験です。執事が隣で手本を示し、最後に記念の写真を撮るところまで付き添います。こちらも男女どちらでも参加できます。',
            image: { src: '/images/related/queens-butler/s17-footman-tea-practice.jpg', alt: '執事の隣で紅茶を注ぐ見習い（館の実写）' },
          },
          {
            title: 'ショップ——館の意匠を日々の品に',
            desc: '館の紋章や調度から生まれた意匠を、受注生産の品に映して届ける入口です。「女王に仕える執事」の世界観を、館の外の日常でも手元に置けるように——という考えで用意されています。いまは準備中で、詳しくは公式サイトの案内を待ってください。',
            image: { src: '/images/related/queens-butler/a05-hall-wide.jpg', alt: '館の回廊と調度（館の実写）' },
          },
        ],
        tint: true,
      },
      {
        kind: 'twocol',
        label: 'THE ROOMS',
        title: '正餐の間から寝室まで、一つ屋根の下の空間',
        sub: 'Inside the house',
        body: [],
        subs: [
          { t: '六つの部屋でできた一つの空間', b: '館は、正餐の間、書斎、暖炉の間、寝室、回廊、衣装部屋といった部屋で構成される一つの空間です。テーブルは1980年代の英国製でイチイの材、シャンデリアはマリアテレサ様式、寝台は19世紀フランスのコルベイユベッド。館の家具や照明、銀器や磁器は、眺めるための背景ではなく、執事の所作と一緒に手に取れる品として置かれています。' },
          { t: '暖炉の間の画面と、寝室の寝台', b: '暖炉の間には120インチの画面が用意され、執事がシャンデリアを落として映画を観る時間も選べます。寝室の寝台は、執事教育でベッドメイキングを稽古するための設備でもあり、滞在中は休憩にも使えます。ビルの屋上に上がれば、レインボーブリッジとお台場の夜景が広がります。' },
          { t: '働く空間の、実物大の手本', b: 'アカデミーの受講生にとって、この館は「執事が働く空間」の実物大の見本です。正餐の間での給仕、書斎での控え方、暖炉の間で保つ距離。授業で身につけた所作が、どんな家具と光の中で求められるのかを、この館は具体的に見せてくれます。' },
        ],
        image: { src: '/images/related/queens-butler/s03-dining-table-manners.jpg', alt: '銀器を並べた食卓の後ろに控える執事（館の実写）' },
        reverse: true,
      },
      {
        kind: 'twocol',
        label: 'THE RULE',
        title: '鍵を渡さない、という一つの約束',
        sub: 'A butler is always in the house',
        body: [],
        subs: [
          { t: '無人で貸さない、という約束', b: 'Queen\'s Butler の館には、どのサービスでも「無人で貸さない」という約束があります。鍵は渡されず、入館と退館は執事が受け、主だけを館に残す使い方は受け付けません。執事が扉の内側にいるからこそ、着いたときの一礼から、帰りに扉の外まで見送るところまでが一続きの時間になる、というのが館の考え方です。' },
          { t: 'そばに立ち続けるわけではない', b: 'ただし、執事は四六時中そばに立っているわけではありません。主が一人で本を読んでいるとき、撮影が静かに進んでいるとき、会議が集中しているとき。そういう場面では控えの間や扉の外へ引き、呼ばれれば応じられる距離を保ちます。近づくか、引くか。この判断を場面ごとに下すことが、アカデミーが観察力の授業で最も重んじていることです。' },
          { t: '安心は、見えない準備から生まれる', b: '「支えられている」という安心は、目に見えない準備から生まれます。来館の前に照明を確かめ、椅子の位置を直し、必要な銀器や衣装を揃え、扉が開く時刻に回廊で待つ。館が語る準備の積み重ねは、受講生が毎回の授業で繰り返している稽古そのものです。' },
        ],
        image: { src: '/images/related/queens-butler/th10-theater-lounge-screen.jpg', alt: '暖炉の間で120インチの画面を前にくつろぐ主（館の実写）' },
        tint: true,
      },
      {
        kind: 'steps',
        label: 'THREE HOURS',
        title: '見習い体験の三時間を、順に追う',
        sub: 'The apprentice experience, step by step',
        lede:
          '見習い体験は、衣装を着るだけの時間ではありません。立つ姿勢や、相手を思って一拍置く間までを含めて「執事の装い」に触れる三時間です。来館時は普段着で構いません。コート、ベスト、シャツ、タイ、白手袋、靴の一式は館が用意しますが、サイズと種類に限りがあるため、予約前に身長と体型、靴のサイズを伝えておきます。',
        image: { src: '/images/related/queens-butler/s16-footman-dressing.jpg', alt: '衣装部屋でモーニングコートの襟を整える執事と見習い（館の実写）' },
        steps: [
          { title: '出迎えと確認', desc: '執事が扉を開けて迎え、体験の希望と衣装のサイズを確かめます。' },
          { title: '着替え', desc: 'コートからベスト、シャツ、タイ、手袋、靴まで、執事と一緒に順に整えます。ボタンの留め方、ベストの見え方、襟元、手袋の収まりを一つずつ確認します。' },
          { title: '姿勢と一礼', desc: '鏡の前で身体の線を確かめ、立つ位置と歩幅、控える距離を試します。相手に向き直る動きと、お辞儀の角度と間もここで。' },
          { title: '紅茶と銀器', desc: 'ティーポットと三段のスタンド、白手袋で扱う銀器を通して、手元を静かに保つ感覚に触れます。トレイとグラスを運ぶ所作も試します。' },
          { title: '記念の一枚', desc: '館の調度を背に、執事の装いで写真を撮ります。着替えから記念品の受け渡しまで、執事が最後まで付き添います。' },
        ],
      },
      {
        kind: 'twocol',
        label: 'GETTING THERE',
        title: '青海のビルの中へ——執事が入口で待つ',
        sub: 'Access',
        body: [],
        subs: [
          { t: '青海の、海沿いの一角', b: '館は東京都江東区青海2-7-4、お台場のビルの中にあります。ゆりかもめとりんかい線のどちらの駅からも歩ける海沿いの一角で、予約制です。詳しい道順は、予約が確定した時点で案内されます。' },
          { t: '到着前に整え、入口で待つ', b: '到着の時刻が近づくと、執事は館内の支度を済ませ、名前と人数、来る手段を確かめて入口で待ちます。扉を開けて一礼し、上着と荷物を預かり、回廊を通ってその日の部屋へ。雨の日は濡れた傘と上着を先に受け取り、館の中を無理なく移れる順番に整えます。' },
          { t: '館の車で迎える手配', b: '電車や車のほかに、館の車で迎える手配もプランに付随する形で案内されています（東京テレポート駅など五か所との往復に限られ、予約時に可否を確認）。アカデミーの拠点も同じ青海にあり、受講生にとっては見慣れた街の一角です。' },
          { t: '所在地', b: '東京都江東区青海2-7-4（お台場）・予約制' },
        ],
        image: { src: '/images/related/queens-butler/rf01-rooftop-night-view.jpg', alt: '屋上から望むレインボーブリッジと夜のお台場（館の実写）' },
        reverse: true,
      },
      {
        kind: 'band',
        label: 'WHY WE INTRODUCE IT',
        title: "学びの場から見た、Queen's Butler という館",
        sub: 'The house, seen from the academy',
        body: [],
        subs: [
          { t: '同じ協会のもとにある館', b: "Queen's Butler とアカデミーは同じ協会のもとにあります。アカデミーが執事を育てる教室だとすれば、この館は執事のもてなしを客として受け取る場所です。同じ基準で育った執事が、同じお台場の街で、教室と館という二つの場所に立っています。" },
          { t: '見習い体験という三時間', b: '受講生にとって特に意味があるのは見習い体験です。モーニングコートを着て、一礼し、銀器に触れる三時間。ここで「この仕事に惹かれた」と感じた人が、次の一歩としてアカデミーの門を叩く。私たちはそういう動線を思い描いています。' },
          { t: '館そのものが、教育と地続き', b: '寝室の寝台がベッドメイキングの稽古台でもあるように、館そのものが執事教育と地続きです。教室で学んだことの先にある「本番の館」——それが受講生から見た Queen\'s Butler です。' },
        ],
        image: { src: '/images/related/queens-butler/f10-two-butlers-bow.jpg', alt: '正餐の間で並んで一礼する二人の執事（館の実写）' },
      },
      {
        kind: 'cards',
        label: 'LESSONS IN THE HOUSE',
        title: '授業の科目が、館のどこに現れるか',
        sub: 'Curriculum, mapped onto the house',
        lede: '受講を考えている方のために、アカデミーの授業と館の運営が重なる箇所を四つに絞って並べました。',
        cards: [
          { title: '観察——近づくか、引くか', desc: '会話の調子や撮影の進み具合を見て距離を決める館の執事の動きは、観察力の授業がそのまま現場に出たものです。' },
          { title: '給仕——紅茶と銀器', desc: '正餐の間で紅茶を注ぎ、銀器を並べ、ナプキンを置く。エッセンシャルコースで繰り返す所作が、館では毎日行われています。' },
          { title: '段取り——見えない準備', desc: '照明、椅子、銀器、衣装。来館前の支度の積み重ねは、アドバンスコースで学ぶ段取りの実例です。' },
          { title: '入口——見習い体験から教室へ', desc: '三時間の体験で装いと所作に触れた人が、アカデミーの学びへ進む。館は、執事を志す人の最初の入口にもなります。' },
        ],
        tint: true,
      },
{
        kind: 'cards',
        label: 'GLOSSARY',
        title: '館の言葉を、四つだけ覚えておく',
        sub: 'Four words used in the house',
        lede: 'Queen\\\'s Butler の公式サイトには、館に固有の言い回しがあります。受講生が現場見学の前に覚えておくとよい四つを選びました。',
        cards: [
          {
            title: '女王',
            desc: '館に来た人を指す言葉。サロンの女性客も、撮影のクルーも、見習い体験の参加者も、扉の内側では等しく「その時間の主」として迎えられます。',
            image: {
              src: '/images/related/queens-butler/s08-bedroom-morning-tea.jpg',
              alt: '寝室で朝の紅茶を供する執事（Queen\\\'s Butler の実写）'
            }
          },
          {
            title: '館',
            desc: '東京都江東区青海2-7-4のビルの中にある、正餐の間・書斎・暖炉の間・寝室・回廊・衣装部屋からなる空間の呼び名です。',
            image: {
              src: '/images/related/queens-butler/s04-lounge-reading.jpg',
              alt: '暖炉の間で読書する主と控える執事（Queen\\\'s Butler の実写）'
            }
          },
          {
            title: '見習い',
            desc: '執事見習い体験の参加者のこと。モーニングコートを着て、一礼や銀器の扱いに三時間触れます。アカデミーの受講生にとっては「最初の一歩」の同義語です。',
            image: {
              src: '/images/related/queens-butler/s19-studio-portrait.jpg',
              alt: '館の調度を背に記念撮影する場面（Queen\\\'s Butler の実写）'
            }
          },
          {
            title: '一組貸切',
            desc: '館内サービスは同時に一組だけ。最大六名までの一組が、館と執事を独占します。会議やロケでも同じ考え方です。',
            image: {
              src: '/images/related/queens-butler/mt01-meeting-dining-four.jpg',
              alt: '正餐の間で会議をする四名と紅茶を運ぶ執事（Queen\\\'s Butler の実写）'
            }
          }
        ],
        tint: true
      },
{
        kind: 'twocol',
        label: 'READING THE SITE',
        title: '公式サイトは「サロン」から読み始めない',
        sub: 'Where to start on the official site',
        body: [
          'Queen\\\'s Butler の公式サイトは、四つのサービスがそれぞれ独立したページをもっています。執事を学ぶ人には、意外かもしれませんが「執事が必ずお側に控える理由」のページから読むことを勧めています。館がなぜ無人で貸さないのか、その理由に執事の仕事の本質が書かれているからです。',
          '次に「執事見習い体験」のページへ。三時間の流れと、衣装の一式、記念撮影までの段取りが書かれていて、自分が体験する側に回ったときの視点が得られます。',
          '最後に「執事サロン」と「スタジオ」のページを、執事の側の視点で読み直してください。どの場面で近づき、どの場面で引くのか。館の執事が守っている距離の取り方を、ページの記述から読み取る練習になります。'
        ],
        image: {
          src: '/images/related/queens-butler/st03-wardrobe-fitting.jpg',
          alt: '衣装部屋で装いを整える場面（Queen\\\'s Butler の実写）'
        },
        reverse: true
      },
{
        kind: 'twocol',
        label: 'FROM THE INSTRUCTORS',
        title: '講師が館の運営から学ぶこと',
        sub: 'What our teaching staff notice there',
        body: [
          'アカデミーの講師にとって、Queen\\\'s Butler の館は「執事教育と地続きの本番」です。寝室の寝台がベッドメイキングの稽古台でもあること、衣装部屋の一式がそのまま見習い体験に使われること。館の設えは、教室と現場のあいだの垣根をほとんど無くしています。',
          '講師が受講生によく話すのは、館の執事の「引き方」です。主が本を開いたら扉の外へ、撮影が始まったら通路の確保へ。近づく所作は教えやすいのですが、引く所作は現場でしか学べません。館はその教材です。',
          'もう一つ、講師が注目しているのは、来館前の準備の細かさです。照明、椅子、銀器、衣装、回廊に立つ時刻。見えない準備が「支えられている安心」を生むという館の考え方は、アカデミーの段取りの授業そのものです。'
        ],
        image: {
          src: '/images/related/queens-butler/s07-bedroom-rest.jpg',
          alt: '寝室で休む主のそばに控える執事（Queen\\\'s Butler の実写）'
        },
        tint: true
      },
{
        kind: 'twocol',
        label: 'COMMON MISREADINGS',
        title: '館をめぐって、行き違いが起きやすい点',
        sub: 'Where the house is misunderstood',
        body: [
          '一つめは、「館は貸しスペースだから執事なしでも借りられる」という理解です。どのサービスでも執事が必ず付き、鍵は渡されません。主だけを館に残す使い方は受け付けていないというのが、館の変わらない約束です。',
          '二つめは、「男性は館に入れない」という思い込みです。女性限定なのはサロンだけで、スタジオと見習い体験は男女どちらでも利用できます。アカデミーの男性受講生も、見習い体験には参加できます。',
          '三つめは、「食事が出る」という期待です。館は食事を提供せず、飲食物の持ち込みができる形です。正餐の間の食卓で執事が手伝うのは、取り分けや設えです。'
        ],
        image: {
          src: '/images/related/queens-butler/s13-tea-hands-receive.jpg',
          alt: '紅茶のカップを差し出す執事の手元（Queen\\\'s Butler の実写）'
        },
        reverse: true
      },
      {
        kind: 'twocol',
        label: 'AS A TEXTBOOK',
        title: '館を教材として歩く——受講生のための見方',
        sub: 'Walking the house as a study',
        body: [],
        subs: [
          { t: '講師が勧めている見方', b: '受講生が館を訪れるとき、講師が勧めている見方があります。もてなしを受けながら、同時に、いま自分に何が起きているかを覚えておくというものです。扉が開く速さ、案内されるときの相手との距離、席に着くまでに何を言われ、何を言われなかったか。客として味わう時間が、そのまま観察の稽古になります。' },
          { t: '何も頼まなかった瞬間を書き留める', b: 'とくに書き留めてほしいのは、自分が何も頼まなかった瞬間です。頼む必要がなかったということは、その前に誰かが整えていたということ。教室で「先に読む」と教えられている動きが、実際にはどれくらい手前で始まっているのか。館は、その時間差を体感できる数少ない場所です。' },
          { t: '記憶に残るのは、小さな動きのほう', b: '帰ってからの振り返りでは、印象に残った所作を一つだけ挙げてもらいます。多くの受講生が挙げるのは、派手な演出ではなく、気づかないうちに済んでいた小さな動きのほうです。目立たない仕事が記憶に残る——その逆説を、自分の体験として持ち帰ってもらうのが狙いです。' },
        ],
        image: { src: '/images/related/queens-butler/scene-09-1600.jpg', alt: '蓄音機と銀器が置かれた館の一角（Queen\'s Butler の館の実写）' },
      },
      {
        kind: 'twocol',
        label: 'THE OTHER SIDE',
        title: '仕えられてみて、はじめてわかることがある',
        sub: 'What you learn from being served',
        body: [],
        subs: [
          { t: '多くは仕える側の作法から入る', b: '執事を学ぶ人の多くは、仕える側の作法から入ります。だからこそ、一度きちんと仕えられる経験が効きます。客の側に座ってみると、自分が良かれと思っていた動きのうち、どれが心地よく、どれが少し重いかが、身をもってわかるからです。' },
          { t: '確認の多さは、中断にもなる', b: 'よくある気づきは、声をかけられる回数の多さについてです。丁寧にしようとするほど確認は増えますが、受ける側に回ると、確認の多さは気づかいではなく中断として届くことがある。どこまでを黙って決め、どこから尋ねるのか。その線の引き方は、仕えられてみないと掴みにくい感覚です。' },
          { t: '視線の重さを、客席で知る', b: 'もう一つは、視線の重さです。常に見られていると、くつろぐことができません。見ていないようでいて必要な瞬間だけ目を戻す、という引きの技術が何のためにあるのかは、客席に座って初めて腑に落ちます。館での時間は、この二つを一度に学べる教材になっています。' },
        ],
        image: { src: '/images/related/queens-butler/scene-07-1600.jpg', alt: '客を迎える支度が整った正餐の間（Queen\'s Butler の館の実写）' },
        reverse: true,
      },
      {
        kind: 'gallery',
        images: [
          { src: '/images/related/queens-butler/drive01-door-open.jpg', alt: '車の扉を開けて客を迎える執事（Queen\'s Butler の実写）' },
          { src: '/images/related/queens-butler/s01-dining-tea-service.jpg', alt: '正餐の間で銀のポットから紅茶を注ぐ執事（館の実写）' },
          { src: '/images/related/queens-butler/bl01-building-dusk.jpg', alt: '夕暮れの青海に立つ館の入るビル', position: 'center 10%' },
          { src: '/images/related/queens-butler/s11-door-farewell.jpg', alt: '扉の外まで見送る執事（館の実写）' },
        ],
      },
      {
        kind: 'official',
        title: "Queen's Butler の公式サイト",
        body: 'サロン、スタジオ、見習い体験、ショップの詳細と予約・問い合わせは公式サイトへ。執事を学ぶ道を考えている方は、当サイトのコース案内をご覧ください。',
        url: 'https://queensbutler.jp/',
        urlLabel: 'queensbutler.jp',
      },
    ],
    faq: [
      { q: "Queen's Butler は何をする場所ですか。", a: '協会が運営する、お台場の館を舞台にしたブランドです。女性のためのサロン、撮影や会議に使えるスタジオ、執事の装いに触れる見習い体験、館の意匠を届けるショップの四つを、一つの館で提供しています。' },
      { q: 'サロンは男性も入れますか。', a: 'サロンは女性の客だけが利用でき、男性は付き添いでも入館できません。スタジオと見習い体験は男女どちらでも利用できます。' },
      { q: '場所はどこですか。', a: '東京都江東区青海2-7-4、お台場のビルの中です。ゆりかもめとりんかい線の駅から歩けます。予約制で、道順は予約確定時に案内されます。' },
      { q: '執事なしで部屋だけ借りられますか。', a: '借りられません。館はどのサービスでも執事が必ず付き、鍵を渡さず、主だけを館に残す使い方は受け付けていません。' },
      { q: '見習い体験の三時間で何をしますか。', a: 'モーニングコート一式に着替え、姿勢と一礼、紅茶、銀器、給仕の所作に触れ、最後に記念撮影をします。衣装は館が用意しますが、サイズに限りがあるので予約前に身長・体型・靴のサイズを伝えます。' },
      { q: 'アカデミーとの関係を教えてください。', a: '同じ協会が運営しています。アカデミーが執事を育てる教室で、館は執事のもてなしを客として受け取る場所です。見習い体験は、アカデミーの学びに気軽に触れる入口でもあります。' },
      {
        q: 'アカデミーの受講生が見習い体験に参加する意味はありますか。',
        a: 'あります。教室で身につけた一礼や銀器の扱いを、客として迎えられる側の視点から体験できます。講師も、館の執事の「引き方」を学ぶ教材として勧めています。'
      },
      {
        q: '館の見学だけをすることはできますか。',
        a: '館は予約制で、館内サービスには執事が必ず付きます。見学のみの可否は公式サイトの案内と予約窓口で確認してください。'
      },
    ],
  },

  // ═══════════════ 株式会社ファミリーロマンス ═══════════════
  {
    slug: 'family-romance',
    name: '株式会社ファミリーロマンス',
    role: '頭取・石井裕一が創業した家族代行の会社',
    tagline:
      '協会の頭取・石井裕一が2004年に興し、いまも代表取締役を務める家族代行の会社。家族や恋人、友人の役を引き受ける代行サービスを、全国の登録スタッフとともに二十年以上続けています。',
    hubImage: { src: '/images/related/family-romance/fr-office.webp', alt: 'ファミリーロマンスのオフィス（ファミリーロマンス公式サイトの写真）' },
    heroImage: { src: '/images/related/family-romance/top-hero-01-office.webp', alt: '依頼に向き合うファミリーロマンスの現場（ファミリーロマンスの公式サイトのトップ画像）', position: 'center 40%' },
    heroTitle: '株式会社ファミリーロマンス',
    heroTitleEn: 'FAMILY ROMANCE INC.',
    heroLead:
      '執事の育成とはまったく別の業種に見えて、根っこにある姿勢は同じ——協会の頭取が創業した家族代行の会社を、学びの場の視点から読み解きます。依頼者の事情に寄り添い、知り得たことを外に出さない。執事に求められる心構えが、ここでも仕事の土台になっています。',
    officialUrl: 'https://family-romance.com/',
    officialLabel: 'ファミリーロマンスの公式サイトを開く',
    lastUpdated: '最終更新: 2026年9月18日',
    blocks: [
      {
        kind: 'twocol',
        label: 'HOW IT BEGAN',
        title: '友人の相談から始まった、「家族代行」という発明',
        sub: 'A concept born from one request',
        body: [],
        subs: [
          { t: '2004年、24歳への相談から', b: '株式会社ファミリーロマンスは、2004年に石井裕一が興した会社です。きっかけは、当時24歳だった石井のもとに、シングルマザーの友人から幼稚園の受験について相談が持ち込まれたことでした。そこから「家族の役を引き受ける」というサービスが生まれ、それまで日本になかった家族代行という考え方が形になっていきます。' },
          { t: '二十年で広がった、引き受け方', b: 'それから二十年あまり。家族、恋人、友人の代行にとどまらず、結婚式への代理出席や、謝罪の場に代わって赴く「お詫び代行」まで、引き受ける場面は広がりました。全国に5,000人を超える登録スタッフを抱え、依頼者一人ひとりの事情に合わせて人を選び、送り出す体制ができています。' },
          { t: '紹介する理由は、一人の人物', b: 'この会社を関連事業として紹介する理由は明快です。創業者であり代表取締役である石井裕一が、アカデミーの運営母体である協会の頭取でもあるからです。執事を育てる仕事と、家族の役を引き受ける仕事。二つの事業が、一人の人物の中で並んでいます。' },
        ],
        image: { src: '/images/related/family-romance/work-office.webp', alt: 'オフィスで打ち合わせをするファミリーロマンスのスタッフ' },
      },
      {
        kind: 'cards',
        label: 'FOUR FACTS',
        title: '公式サイトが掲げる、四つの特徴',
        sub: 'What the company says about itself',
        lede: 'ファミリーロマンスの公式サイトが自らの特徴として挙げている四点を、事実だけ抜き出して並べました。',
        cards: [
          { title: '先駆け', desc: '2004年の創業以来、家族・恋人・友人の代行から式への代理出席まで、それまで日本に存在しなかった代行という発想を事業として確立してきました。' },
          { title: '5,000人超の登録スタッフ', desc: '全国各地に5,000人を超える登録スタッフがおり、依頼の内容と事情に合わせて人を選べる体制を整えています。' },
          { title: '国際映画祭で上映された主演作', desc: '代表の石井裕一は、ドイツの映画作家ヴェルナー・ヘルツォークが撮った『Family Romance, LLC』に本人として出演し主演を務め、その作品はカンヌ国際映画祭（第72回）の監督週間で上映されました。' },
          { title: '漏洩ゼロの情報管理', desc: '全スタッフと秘密保持契約を結び、仕事で知った情報を外に出さない体制を徹底。二十年以上にわたって情報漏洩がないことを実績として掲げています。' },
        ],
        tint: true,
      },
      {
        kind: 'twocol',
        label: 'A SHARED ROOT',
        title: '執事の心構えと、代行の心構えは、同じ方角を向いている',
        sub: 'Two businesses, one attitude',
        body: [],
        subs: [
          { t: '二つの肩書が、一人の中で', b: '協会の頭取と、家族代行の会社の代表。二つの肩書は一見すると遠く離れていますが、石井裕一の中では「人と人のあいだを支える」という一本の姿勢でつながっています。執事という専門職を育てる仕事も、家族の役を引き受ける仕事も、その姿勢の表れ方が違うだけです。' },
          { t: '相手が言う前に読む、という基本', b: 'アカデミーが授業で最初に教えるのは、相手が口に出す前に望みを読み取り、目立たずに支えるという執事の基本姿勢です。ファミリーロマンスが二十年かけて培ってきた「依頼者の事情に寄り添う」という仕事の流儀は、場所は違っても、この基本姿勢と同じ方角を向いています。' },
          { t: '相手を主語にする、という一点', b: '執事は、頼まれる前に整え、頼まれない間は引いている。代行スタッフは、依頼者が望む場面を、その人の事情を汲んだうえで静かに成り立たせる。相手を主語にして動く、という一点で、二つの仕事は重なります。' },
        ],
        image: { src: '/images/related/family-romance/philosophy-bonds.webp', alt: '人と人のつながりを表すファミリーロマンスのイメージ' },
        reverse: true,
      },
      {
        kind: 'twocol',
        label: 'DISCRETION',
        title: '知り得たことを外に出さない、という共通の責任',
        sub: 'Confidentiality as a shared duty',
        body: [],
        subs: [
          { t: '全員と秘密保持契約を結ぶ', b: 'ファミリーロマンスは、登録している全員と秘密保持契約を交わし、仕事の中で知った事柄を外に漏らさない仕組みを持っています。二十年以上にわたって漏洩がないという実績は、依頼者の私的な事情に深く踏み込む仕事だからこそ求められる責任の重さを物語っています。' },
          { t: '執事も、私的な情報に毎日触れる', b: '執事もまた、家庭や会社の内側に入り込み、資産や予定、来客といった私的な情報に毎日触れる仕事です。アカデミーの授業では、守秘を技術ではなく心構えの土台として扱い、最初の学期から繰り返し教えています。' },
          { t: '守秘を、契約という形にする', b: '扱う情報の種類は違っても、「知り得たことを自分の中に留める」という責任の重さは同じです。ファミリーロマンスの仕組みは、守秘を気持ちだけに頼らず契約という形にした例として、受講生にとって参考になります。' },
        ],
        image: { src: '/images/related/family-romance/profile-main.jpg', alt: '代表取締役の石井裕一（ファミリーロマンスの公式サイトの写真）' },
        tint: true,
      },
      {
        kind: 'steps',
        label: 'SERVICES',
        title: '公式サイトに載っている代行の種類',
        sub: 'What they offer',
        lede: 'ファミリーロマンスが公式サイトで案内している代行サービスのうち、代表的なものを四つ。詳しい内容や費用は公式サイトで確認してください。',
        image: { src: '/images/related/family-romance/svc-family.webp', alt: 'ファミリーロマンスの家族代行サービスのイメージ' },
        steps: [
          { title: '家族の代行', desc: '家族の役を引き受けたスタッフが、依頼者の事情に合わせて同席し、同行します。会社の始まりとなったサービスです。' },
          { title: '恋人・友人の代行', desc: '恋人や友人として場に加わる代行です。依頼者ごとの事情を汲んだうえで人を選びます。' },
          { title: '結婚式への代理出席', desc: '式にゲストとして出席する代行で、全国の登録スタッフが対応します。' },
          { title: 'お詫びの代行', desc: '依頼者に代わって謝罪の場に赴くサービスです。内容は公式サイトの案内を参照してください。' },
        ],
      },
      {
        kind: 'twocol',
        label: 'FROM THE CLASSROOM',
        title: '「寄り添う」を、技術として教える',
        sub: 'Attending to another, as a teachable skill',
        body: [],
        subs: [
          { t: '察することを、技術として教える', b: 'アカデミーでは「相手の望みを察する」ことを、気持ちの持ち方としてではなく、観察の技術として教えています。視線の先、手の動き、言葉にしない間。そこから次に必要になるものを読み、頼まれる前に用意する。この観察と準備の反復が、執事のもてなしの土台です。' },
          { t: '寄り添うことの構造は同じ', b: 'ファミリーロマンスが「一人ひとりの事情に寄り添う」と言うとき、そこにも同じ構造があります。依頼者が何を望み、どの場面をどう過ごしたいのか。それを丁寧に汲み取って初めて、代行という仕事は成立します。相手を主語に置いて考える、という一点で、執事の仕事と代行の仕事は同じ側に立っています。' },
          { t: '執事の枠を越えて見てほしい', b: '受講生には、執事という職業の枠を越えて、この「相手を主語にする」姿勢が社会のどこで求められているかを見てほしいと思っています。ファミリーロマンスは、その一つの例です。' },
        ],
        image: { src: '/images/related/family-romance/movie-ishii-portrait.jpg', alt: '映画『Family Romance, LLC』関連の石井裕一のポートレート（ファミリーロマンス公式サイトの写真）' },
        reverse: true,
      },
      {
        kind: 'band',
        label: 'WHY WE INTRODUCE IT',
        title: '学びの場から見た、ファミリーロマンスという会社',
        sub: 'The company, seen from the academy',
        body: [],
        subs: [
          { t: 'つないでいるのは一人の人物', b: 'ファミリーロマンスとアカデミーを直接つないでいるのは、石井裕一という一人の人物です。協会の頭取として執事文化を次代に渡す仕事と、会社の代表として家族代行を率いる仕事。この二つは「人と人のあいだを支える」という一点で結ばれています。' },
          { t: '執事以外の仕事にも通じる', b: 'この会社を受講生に紹介するのは、執事の心構えが執事以外の仕事にも通じる普遍的なものだと知ってほしいからです。相手の望みを先に読み、目立たずに支え、知ったことを守る。教室で学ぶこの姿勢は、教室の外でも求められています。' },
          { t: '依頼は公式サイトへ', b: '代行を頼みたい方はファミリーロマンスの公式サイトへ。執事という仕事を知りたい方は、当サイトの各ページをどうぞ。' },
        ],
        image: { src: '/images/related/family-romance/movie-gallery-2.jpg', alt: '映画『Family Romance, LLC』の撮影風景（ファミリーロマンス公式サイトの写真）' },
      },
      {
        kind: 'cards',
        label: 'THREE LESSONS',
        title: '執事を志す人が、この会社から学べる三つのこと',
        sub: 'What learners can take away',
        lede: '業種は違いますが、執事を目指す人にとって参考になる点を三つに絞りました。',
        cards: [
          { title: '相手の事情に合わせる', desc: '依頼ごとに人を選び、事情に合わせて対応する体制は、家庭ごとに違う生活のリズムに合わせて仕える執事の姿勢と重なります。' },
          { title: '守秘を仕組みにする', desc: '全員と秘密保持契約を結ぶというやり方は、守秘を「心がけ」で終わらせず「約束」として引き受ける手本になります。' },
          { title: '一つの姿勢を複数の仕事に', desc: '執事文化の継承と家族代行という別々の事業を同じ姿勢で率いる石井裕一の歩みは、執事の心構えがどこでも通用することを示しています。' },
        ],
        tint: true,
      },
{
        kind: 'cards',
        label: 'GLOSSARY',
        title: '代行の会社を読むための四つの言葉',
        sub: 'Four terms from the Family Romance site',
        lede: '執事の学校とは業種が違うぶん、ファミリーロマンスのサイトには耳慣れない言葉があります。受講生向けに四つを整理しました。',
        cards: [
          {
            title: '家族代行',
            desc: '家族の役を引き受けるサービスのこと。2004年の創業のきっかけになった依頼から生まれ、いまも会社の中心にあります。',
            image: {
              src: '/images/related/family-romance/top-hero-03-family.webp',
              alt: 'ファミリーロマンスの一場面（公式サイトの写真）'
            }
          },
          {
            title: '登録スタッフ',
            desc: '依頼に応じて役を引き受ける人たち。全国に5,000人を超え、事情に合わせて選ばれます。執事の名簿とは違い、依頼者が直接選ぶ仕組みではありません。',
            image: {
              src: '/images/related/family-romance/philosophy-group.webp',
              alt: 'ファミリーロマンスの一場面（公式サイトの写真）'
            }
          },
          {
            title: '秘密保持契約',
            desc: '登録している全員と結ぶ、知り得たことを外に出さないという約束。二十年以上漏洩がないという実績の土台です。',
            image: {
              src: '/images/related/family-romance/work-compliance.webp',
              alt: 'ファミリーロマンスの一場面（公式サイトの写真）'
            }
          },
          {
            title: '代理出席',
            desc: '結婚式などにゲストとして出席する代行のこと。全国の登録スタッフが対応します。',
            image: {
              src: '/images/related/family-romance/top-hero-02-wedding.webp',
              alt: 'ファミリーロマンスの一場面（公式サイトの写真）'
            }
          }
        ],
        tint: true
      },
{
        kind: 'twocol',
        label: 'READING THE SITE',
        title: '公式サイトを、執事の目で読む',
        sub: 'Reading the site as a butler would',
        body: [
          'ファミリーロマンスの公式サイトは、代行の種類ごとに案内が分かれています。執事を学ぶ人には、個々のサービスよりも先に、会社の成り立ちと情報管理の考え方を読むことを勧めています。依頼者の事情にどう向き合い、知ったことをどう守るのか。そこに執事と共通する姿勢が書かれています。',
          'そのうえで各サービスの案内を読むと、依頼者が何を望んで代行を頼むのかが見えてきます。相手の望みを言葉の外から読み取る練習として、これは執事の観察の稽古と同じ構造をもっています。',
          '代表の石井裕一の紹介ページも、協会の頭取の別の顔を知るという意味で、一度は目を通しておいてほしいページです。'
        ],
        image: {
          src: '/images/related/family-romance/movie-gallery-6.jpg',
          alt: 'ファミリーロマンスの一場面（公式サイトの写真）'
        },
        reverse: true
      },
{
        kind: 'twocol',
        label: 'FROM THE INSTRUCTORS',
        title: '講師がファミリーロマンスを話題にする理由',
        sub: 'Why instructors bring it up in class',
        body: [
          'アカデミーの講師がファミリーロマンスを話題にするのは、守秘の授業のときです。執事は家庭や会社の内側で私的な情報に触れます。その責任をどう仕組みにするか——全員と秘密保持契約を結ぶというファミリーロマンスのやり方は、具体的な参考例になります。',
          'もう一つは、「相手を主語にする」姿勢を教えるときです。依頼者一人ひとりの事情に合わせて人を選ぶという会社の流儀は、家庭ごとに違う生活のリズムに合わせて仕える執事の姿勢と、方角が同じです。',
          '講師は、業種が違うからこそ学べることがある、と話します。同じ執事の話ばかり聞いていると見えなくなる「支える仕事の共通項」が、別の業種を通すとはっきり見えるからです。'
        ],
        image: {
          src: '/images/related/family-romance/movie-gallery-3.jpg',
          alt: 'ファミリーロマンスの一場面（公式サイトの写真）'
        },
        tint: true
      },
{
        kind: 'twocol',
        label: 'COMMON MISREADINGS',
        title: 'この会社は、こう取り違えられやすい',
        sub: 'Three things people get wrong',
        body: [
          '一つめは、「執事の会社だ」という誤解です。ファミリーロマンスは家族代行の会社で、執事の育成や執事サービスは行っていません。執事に関わる事業は協会、アカデミー、THE BUTLERが担っています。',
          '二つめは、「協会が運営している」という思い込みです。協会とファミリーロマンスをつないでいるのは、頭取であり代表取締役でもある石井裕一という一人の人物で、団体としての運営関係はありません。',
          '三つめは、「映画の会社だ」という取り違えです。代表が本人役で主演した映画『Family Romance, LLC』は会社の仕事を題材にした作品ですが、会社そのものは代行サービスの会社です。'
        ],
        image: {
          src: '/images/related/family-romance/cinema-cannes.webp',
          alt: 'ファミリーロマンスの一場面（公式サイトの写真）'
        },
        reverse: true
      },
      {
        kind: 'twocol',
        label: 'TWENTY YEARS',
        title: '二十年続いたという事実そのものが、教材になる',
        sub: 'Longevity as a lesson',
        body: [],
        subs: [
          { t: '始めることと、続けること', b: '前例のない仕事を始めることと、それを二十年続けることは、まったく別の難しさをもっています。アカデミーが受講生にこの会社の話をするとき、力点を置くのは創業の着想よりも、続いたという事実のほうです。人に関わる仕事は、一度の成功では評価が定まらず、積み重ねの中でしか信用が生まれないからです。' },
          { t: '信用は、出来事でなく期間で測られる', b: '執事の仕事も同じ構造をしています。初日の一礼が見事でも、それだけでは何も決まりません。毎日同じ質で同じことが起きると分かったとき、はじめて任される範囲が広がる。信用は出来事ではなく期間で測られる、という考え方は、教室で繰り返し伝えている一点です。' },
          { t: '熱意を支える段取りがある', b: '続けるためには、当人の熱意だけでは足りません。人を選ぶ手順、約束を守らせる仕組み、記録の残し方。長く続いている組織には、気持ちを支える段取りが必ずあります。受講生には、華やかな逸話よりもその地味な部分を見てほしいと伝えています。' },
        ],
        image: { src: '/images/related/family-romance/interview-company.jpg', alt: '会社について語る場面（ファミリーロマンスの公式サイトの写真）' },
      },
      {
        kind: 'twocol',
        label: 'WHEN NOT TO SPEAK',
        title: '話さないことを、技術として教える',
        sub: 'Silence as a skill, not a mood',
        body: [],
        subs: [
          { t: 'もう一段手前から教える', b: '守秘というと、外に漏らさないことだと理解されがちです。教室ではもう一段手前から教えます。そもそも聞かない、必要以上に見ない、覚えたことを話題にしない。漏らさない努力をする前に、抱え込む量を減らしておくほうが確実だからです。' },
          { t: '居合わせ方を稽古する', b: '具体的には、居合わせ方の稽古になります。会話が始まったら視線を外して一歩引く、書類が広げられたら向きを変える、名前や金額が聞こえたら話題にしない。どれも派手さのない動きですが、これができる人とできない人とでは、任される仕事の範囲がはっきり変わります。' },
          { t: '心構えを、仕組みに変える', b: '私的な場面に深く入る仕事が、契約という形で守秘を担保しているという例は、受講生にとって分かりやすい手本になります。気持ちで守るのではなく、守れる形にしておく。心構えを仕組みに変える、という発想を持ち帰ってほしいと思っています。' },
        ],
        image: { src: '/images/related/family-romance/interview-family.jpg', alt: '依頼者との関わりについて語る場面（ファミリーロマンスの公式サイトの写真）' },
        reverse: true,
      },
      {
        kind: 'gallery',
        images: [
          { src: '/images/related/family-romance/movie-gallery-5.jpg', alt: '映画『Family Romance, LLC』の一場面（ファミリーロマンス公式サイトの写真）' },
          { src: '/images/related/family-romance/movie-gallery-8.jpg', alt: '映画『Family Romance, LLC』の打ち合わせ（ファミリーロマンス公式サイトの写真）' },
          { src: '/images/related/family-romance/ishii-02-1.png', alt: '石井裕一（ファミリーロマンス公式サイトの写真）' },
          { src: '/images/related/family-romance/ishii-04-1.png', alt: '講座で語る石井裕一（ファミリーロマンス公式サイトの写真）' },
        ],
      },
      {
        kind: 'twocol',
        label: 'A QUESTION WE ASK',
        title: '受講生に投げている、一つの問い',
        sub: 'One question we put to learners',
        body: [],
        subs: [
          { t: '授業の最後に必ず尋ねること', b: 'この会社の話をした授業の最後に、講師は決まって同じことを尋ねます。あなたが支えているのは、目の前の相手ですか、それとも相手が守りたい場面ですか——と。答えはすぐに出ません。出ないまま考え続けてもらうために置いている問いだからです。' },
          { t: '人を支えるか、場を保つか', b: '執事の仕事でも、この二つはよく食い違います。主人のためを思って口を挟めば、その場の空気は壊れる。場を保つために黙れば、相手には気づかれないまま終わる。どちらを選ぶかは状況次第で、正解表は作れません。作れないからこそ、判断の基準を自分の中に持っておく必要があります。' },
          { t: '答えは年ごとに違ってよい', b: '受講生の答えは年ごとに違います。人を支えたいと言う人もいれば、場を成り立たせたいと言う人もいる。どちらも執事の仕事の一面で、どちらか一方だけでは足りない。別の業種の話を通すと、この問いが自分ごととして立ち上がってくる——それが、この会社を紹介している理由です。' },
        ],
        image: { src: '/images/related/family-romance/office.webp', alt: 'ファミリーロマンスの仕事場（ファミリーロマンスの公式サイトの写真）' },
      },
      {
        kind: 'official',
        title: 'ファミリーロマンスの公式サイト',
        body: '代行の種類、依頼の方法、費用は公式サイトにまとまっています。執事の学びについては、当サイトのコース案内とお問い合わせをご利用ください。',
        url: 'https://family-romance.com/',
        urlLabel: 'family-romance.com',
      },
    ],
    faq: [
      { q: 'どんな会社ですか。', a: '2004年に石井裕一が興した家族代行の会社です。家族、恋人、友人の代行から式への代理出席、お詫びの代行まで、全国で引き受けています。' },
      { q: 'なぜ執事の学校が紹介しているのですか。', a: '創業者で代表取締役の石井裕一が、アカデミーの運営母体である協会の頭取だからです。業種は違いますが、人と人のあいだを支えるという姿勢でつながっています。' },
      { q: 'スタッフの規模は。', a: '全国に5,000人を超える登録スタッフがいます。依頼の事情に合わせて人を選ぶ体制です。' },
      { q: '秘密は守られますか。', a: '全スタッフと秘密保持契約を結び、仕事で知った情報を外に出さない体制を徹底しています。二十年以上、漏洩がないことを実績としています。' },
      { q: '映画に出たと聞きました。', a: '代表の石井裕一が、ヘルツォーク監督作『Family Romance, LLC』に自分自身として出演し、主演を務めました。カンヌ国際映画祭（第72回）の監督週間で上映された作品です。' },
      { q: '依頼はどこから。', a: '代行の内容と依頼の方法は、ファミリーロマンスの公式サイトで案内されています。' },
      {
        q: 'アカデミーの受講生がファミリーロマンスで働くことはありますか。',
        a: 'ファミリーロマンスの登録スタッフの募集と条件は同社の公式サイトで案内されています。アカデミーの学びは執事を目指すもので、代行の仕事とは別の道です。'
      },
      {
        q: 'ファミリーロマンスと協会は、組織として関係がありますか。',
        a: '団体としての運営関係はありません。両者をつないでいるのは、協会の頭取であり同社の創業者兼代表取締役でもある石井裕一という一人の人物です。'
      },
    ],
  },

  // ═══════════════ 石井裕一オフィシャルサイト ═══════════════
  {
    slug: 'ishii-yuichi',
    name: '石井裕一オフィシャルサイト',
    role: '頭取・石井裕一の俳優・実業家としての顔',
    tagline:
      '協会の頭取・石井裕一の、執事以外の顔をまとめた公式サイト。ヘルツォーク監督作『Family Romance, LLC』での主演、ハーバード大学での講演とガラス賞、著書『人間レンタル屋』。俳優として、実業家としての歩みが載っています。',
    hubImage: { src: '/images/related/ishii-yuichi/profile-trenchcoat.jpg', alt: '石井裕一のポートレート', position: 'center 20%' },
    heroImage: { src: '/images/related/ishii-yuichi/profile-trenchcoat.jpg', alt: '街角に立つ石井裕一', position: 'center 15%' },
    heroTitle: '石井裕一 オフィシャルサイト',
    heroTitleEn: 'YUICHI ISHII OFFICIAL SITE',
    heroLead:
      '執事ベルという名前の裏側には、俳優であり実業家である石井裕一がいます。国際映画祭で上映された映画の主演、海外の大学での講演、一冊の著書。協会を率いる人物のもう一つの顔を、学びの場の視点からたどります。',
    officialUrl: 'https://getstage.com/',
    officialLabel: '石井裕一オフィシャルサイトを開く',
    lastUpdated: '最終更新: 2026年9月18日',
    blocks: [
      {
        kind: 'twocol',
        label: 'PROFILE',
        title: '執事ベルの、もう一つの顔',
        sub: 'The man behind the butler',
        body: [],
        subs: [
          { t: '俳優と実業家としての経歴', b: '石井裕一オフィシャルサイトは、協会の頭取であり執事ベルとしても知られる石井裕一の、俳優と実業家としての経歴をまとめた公式サイトです。1981年生まれ。2004年に株式会社ファミリーロマンスを興し、創業者兼代表取締役として家族代行の事業を続けてきました。' },
          { t: '2019年という節目の年', b: '2019年は節目の年でした。ドイツの映画監督ヴェルナー・ヘルツォークの作品『Family Romance, LLC』で自分自身の役を演じて主演し、その作品が第72回カンヌ国際映画祭の監督週間で上映されます。同じ年にはハーバード大学の会議に招かれて講演し、ガラス賞を受けました。' },
          { t: '二つのサイトの、役割の違い', b: '著書として『人間レンタル屋』（鉄人社、2019年）があります。執事ベルのサイトが「執事としての石井」を伝えるのに対して、このサイトは「執事の外側にいる石井」を伝えています。' },
        ],
        image: { src: '/images/related/ishii-yuichi/profile-white.jpg', alt: '白いシャツの石井裕一（オフィシャルサイトの写真）' },
      },
      {
        kind: 'cards',
        label: 'FOUR THREADS',
        title: '俳優・実業家・講演者・著者——四本の糸',
        sub: 'Four strands of one career',
        lede: '公式サイトに載っている活動を、四本の糸として整理しました。どの糸も、最終的には協会を率いる人物の輪郭を形づくっています。',
        cards: [
          { title: '俳優として', desc: '2019年に公開された、ヴェルナー・ヘルツォークの映画『Family Romance, LLC』で主役を務めました。演じたのは石井裕一その人です。作品はカンヌ国際映画祭（第72回）の監督週間に選ばれています。' },
          { title: '実業家として', desc: '株式会社ファミリーロマンスを2004年に創業し、いまも代表取締役として家族代行の事業を率いています。日本になかった「家族代行」という考え方を社会に定着させた人物です。' },
          { title: '講演者として', desc: '2019年、ハーバード大学で開かれた会議（Harvard Global Health & Leadership Conference）に登壇し、家族代行という仕事が社会に対して持つ意味を語りました。この講演がガラス賞を受けています。' },
          { title: '著者として', desc: '『人間レンタル屋』（鉄人社、2019年）を執筆。代行の現場で見てきた人と人の関係を一冊にまとめています。' },
        ],
        tint: true,
      },
      {
        kind: 'twocol',
        label: 'ON SCREEN',
        title: '自分自身を演じた映画、『Family Romance, LLC』',
        sub: 'Playing himself for Werner Herzog',
        body: [],
        subs: [
          { t: '自分自身の役で主演した', b: '2019年、ヴェルナー・ヘルツォーク監督の『Family Romance, LLC』で、石井裕一は自分自身の役として主演しました。作品は第72回カンヌ国際映画祭の監督週間で上映されています。' },
          { t: '事業家と俳優が重なった出来事', b: '家族代行という仕事をそのまま題材にしたこの映画は、事業家としての石井と俳優としての石井が一つに重なった出来事でした。作品の情報は公式サイトに載っています。' },
          { t: '文化を社会に伝えるという仕事', b: '受講生から見れば、協会の頭取が国際映画祭で上映された作品の主演俳優でもあるという事実は、執事文化を社会に伝えるという協会の仕事が、どれだけ広い範囲に及びうるかを示す一例です。' },
        ],
        image: { src: '/images/related/ishii-yuichi/behind-scenes-02.jpg', alt: '『Family Romance, LLC』の撮影現場（オフィシャルサイトの写真）' },
        reverse: true,
      },
      {
        kind: 'twocol',
        label: 'ON STAGE',
        title: 'ハーバードの壇上で、家族代行を語る',
        sub: 'The lecture and the Glass Award',
        body: [],
        subs: [
          { t: 'ハーバードでの講演', b: '2019年、石井裕一はハーバード大学で開かれた会議、Harvard Global Health & Leadership Conference の壇上に立ち、家族代行という仕事が社会に対して持つ意味を語りました。この講演に対してガラス賞が贈られています。' },
          { t: '海外の学術の場で取り上げられる', b: '人と人のあいだを支える事業という考え方が、海外の学術の場で取り上げられたことは、石井の歩みの中でも大きな出来事の一つです。' },
          { t: '活動の幅を知っておく意味', b: '執事ベルとしての発信、協会の頭取としての仕事、そしてこうした講演。活動の幅を知っておくことは、アカデミーが掲げる「執事を、明確な基準で評価される専門職として社会に位置づける」という目標を考えるうえでも役に立ちます。' },
        ],
        image: { src: '/images/related/ishii-yuichi/harvard-lecture.jpg', alt: 'ハーバード大学の講演で語る石井裕一（オフィシャルサイトの写真）' },
        tint: true,
      },
      {
        kind: 'steps',
        label: 'TIMELINE',
        title: '公式サイトに記された年譜',
        sub: 'Milestones in order',
        lede: '公式サイトに載っている出来事を、時系列で四つに整理しました。',
        image: { src: '/images/related/ishii-yuichi/rental-family-movie-04.jpg', alt: '『Family Romance, LLC』のスチール（オフィシャルサイトの写真）' },
        steps: [
          { title: '1981年', desc: '石井裕一、生まれる。' },
          { title: '2004年', desc: '株式会社ファミリーロマンスを創業。家族代行という、日本になかった事業を始めます。' },
          { title: '2019年', desc: '『Family Romance, LLC』に本人役で主演（第72回カンヌ国際映画祭 監督週間で上映）。ハーバード大学の会議で講演し、ガラス賞を受賞。著書『人間レンタル屋』（鉄人社）を刊行。' },
          { title: '協会の頭取として', desc: '執事ベルとしてYouTubeなどで執事文化を発信しながら、アカデミーの運営母体である協会を率いています。' },
        ],
      },
      {
        kind: 'twocol',
        label: 'FROM THE CLASSROOM',
        title: '執事文化を、閉じた世界から開かれた職業へ',
        sub: 'Making a profession visible',
        body: [],
        subs: [
          { t: '専門職として社会に根づかせる', b: 'アカデミーの目標は、執事という職業を、明確な基準で評価される専門職として社会に根づかせることです。そのためには教室で技能を磨くだけでは足りず、執事という仕事そのものが世の中に知られ、理解される必要があります。' },
          { t: '国内外の場に立ってきたこと', b: '協会の頭取が、映画の主演や海外の学術会議での講演といった形で、国内外のメディアや学術の場に立ってきたこと。それは、執事文化を閉じた世界から開かれた職業へ移すという協会の方向と重なっています。執事ベルとしての発信も、このサイトにまとめられた活動も、同じ方向を向いています。' },
          { t: '学んだ技能が、どう語られるか', b: '受講生には、自分が学んでいる技能が社会にどう受け止められ、どう語られるのかにも目を向けてほしいと思っています。石井裕一オフィシャルサイトは、そのための窓の一つです。' },
        ],
        image: { src: '/images/related/ishii-yuichi/harvard-campus.jpg', alt: 'ハーバード大学のキャンパスに立つ石井裕一（オフィシャルサイトの写真）' },
        reverse: true,
      },
      {
        kind: 'band',
        label: 'WHY WE INTRODUCE IT',
        title: '学びの場から見た、石井裕一という人物',
        sub: 'The person, seen from the academy',
        body: [],
        subs: [
          { t: '頭取であり、発信する人', b: '石井裕一は、アカデミーの運営母体である協会の頭取であり、執事ベルとしてYouTubeなどで執事文化を伝えている人物です。俳優と実業家としての詳しい経歴は、公式サイトで読むことができます。' },
          { t: '専門職として位置づける歩み', b: '国内外のメディアで取り上げられてきたその歩みは、執事を「基準で評価される専門職」として社会に位置づけるというアカデミーの理念と、方向を同じくしています。' },
          { t: '二つを合わせて読む', b: '執事としての石井を知りたければ執事ベルのサイトを、俳優・実業家としての石井を知りたければこのサイトを。二つを合わせて読むと、協会を率いる人物の全体像が見えてきます。' },
        ],
        image: { src: '/images/related/ishii-yuichi/poster.jpg', alt: '『Family Romance, LLC』のポスター（オフィシャルサイトの写真）' },
      },
      {
        kind: 'cards',
        label: 'TWO SITES',
        title: '執事ベルのサイトと、このサイトの読み分け',
        sub: 'Which site for what',
        lede: '同じ人物の二つの公式サイトを、どう読み分ければよいかを三点にまとめました。',
        cards: [
          { title: '執事ベル公式サイト', desc: '協会の頭取・執事としての活動。執事文化の研究と発信、「執事の知恵」、講演と研修、メディア出演、書籍の案内。' },
          { title: '石井裕一オフィシャルサイト', desc: '俳優・実業家としての経歴。『Family Romance, LLC』、ハーバード講演とガラス賞、『人間レンタル屋』、ファミリーロマンスの創業。' },
          { title: 'アカデミーとの接点', desc: 'どちらのサイトも、運営母体である協会を率いる人物を知る手がかりです。執事という職業が社会でどこまで広がりうるかを考える材料になります。' },
        ],
        tint: true,
      },
{
        kind: 'cards',
        label: 'GLOSSARY',
        title: 'オフィシャルサイトを読むための四つの言葉',
        sub: 'Four terms from the official site',
        lede: '俳優と実業家の経歴を扱うサイトなので、執事の学校の受講生には馴染みのない言葉が並びます。四つだけ押さえておいてください。',
        cards: [
          {
            title: '本人役',
            desc: '映画『Family Romance, LLC』で石井裕一が演じた役のこと。架空の人物ではなく、自分自身として出演しました。',
            image: {
              src: '/images/related/ishii-yuichi/harvard-lecture-02.jpg',
              alt: 'ハーバード大学での講演の一場面（オフィシャルサイトの写真）'
            }
          },
          {
            title: '監督週間',
            desc: 'カンヌ国際映画祭の部門の一つ。『Family Romance, LLC』は第72回の監督週間で上映されました。',
            image: {
              src: '/images/related/ishii-yuichi/harvard-lecture-03.jpg',
              alt: 'ハーバード大学での講演で聴衆に向かう石井裕一（オフィシャルサイトの写真）'
            }
          },
          {
            title: 'ガラス賞',
            desc: '2019年、ハーバード大学の会議での講演に対して贈られた賞です。',
            image: {
              src: '/images/related/ishii-yuichi/harvard-poster.jpg',
              alt: 'ハーバード大学の会議のポスター（オフィシャルサイトの写真）'
            }
          },
          {
            title: '『人間レンタル屋』',
            desc: '鉄人社から2019年に出た著書。代行の現場で見てきた人と人の関係をまとめています。',
            image: {
              src: '/images/related/ishii-yuichi/stage-presen-01.jpg',
              alt: '壇上でプレゼンテーションをする石井裕一（オフィシャルサイトの写真）'
            }
          }
        ],
        tint: true
      },
{
        kind: 'twocol',
        label: 'READING THE SITE',
        title: '俳優のページから読むか、実業家のページから読むか',
        sub: 'Two ways into the site',
        body: [
          'オフィシャルサイトは、俳優としての活動と実業家としての活動が分かれて載っています。執事を学ぶ人には、実業家のページ——ファミリーロマンスの創業と、その考え方——から読むことを勧めています。協会の頭取がどんな仕事の仕方をしてきた人なのかが、そこに書かれているからです。',
          '俳優のページは、そのあとで読むと意味が変わります。映画への主演や海外での講演は、家族代行という仕事が国内外でどう受け止められたかの記録でもあります。執事文化を社会に開くという協会の方向と、重ね合わせて読んでください。',
          '執事としての石井裕一を知りたい場合は、このサイトではなく執事ベルの公式サイトへ。二つのサイトを行き来することで、頭取の全体像が見えてきます。'
        ],
        image: {
          src: '/images/related/ishii-yuichi/germany-street.jpg',
          alt: '街角に立つ石井裕一（オフィシャルサイトの写真）'
        },
        reverse: true
      },
{
        kind: 'twocol',
        label: 'FROM THE INSTRUCTORS',
        title: '講師が頭取の歩みを授業で語る場面',
        sub: 'When instructors mention the head',
        body: [
          'アカデミーの講師が石井裕一の俳優・実業家としての歩みに触れるのは、「執事という職業を社会に開く」という話をするときです。執事の仕事は閉じた世界で完結しがちですが、協会の頭取自身が映画や講演を通じて外の世界に立ってきたことは、受講生にとって一つの励みになります。',
          '講師はまた、俳優の仕事と執事の仕事の共通点にも触れます。相手の望む場面を、自分を前に出さずに成り立たせる。本人役で映画に出るという特殊な経験も、突き詰めれば「求められた役割を静かに引き受ける」という執事の姿勢と重なります。',
          'ただし講師は、頭取の経歴を真似ることを求めているのではありません。伝えたいのは、執事の心構えが教室の外でも通用するという事実です。石井裕一の歩みは、その一つの証拠として語られます。'
        ],
        image: {
          src: '/images/related/ishii-yuichi/portrait-01.jpg',
          alt: '石井裕一のポートレート（オフィシャルサイトの写真）'
        },
        tint: true
      },
{
        kind: 'twocol',
        label: 'COMMON MISREADINGS',
        title: 'このサイトを読むときに、外しやすい点',
        sub: 'Reading the site without tripping',
        body: [
          '一つめは、「執事ベルと石井裕一は別人だ」という誤解です。同じ人物で、執事として活動するときの名前が執事ベル、俳優・実業家として活動するときの名前が石井裕一です。',
          '二つめは、「協会の頭取は執事の仕事だけをしている」という思い込みです。頭取は同時にファミリーロマンスの代表取締役であり、俳優としての活動もあります。どの顔で見るかによって、読むべきサイトが変わります。',
          '三つめは、「オフィシャルサイトで執事や講演を頼める」という取り違えです。執事の依頼はTHE BUTLER、講演や研修、メディア出演の依頼は執事ベルの公式サイトが窓口です。オフィシャルサイトは経歴と作品の紹介が中心です。'
        ],
        image: {
          src: '/images/related/ishii-yuichi/stage-07.jpg',
          alt: '壇上に立つ石井裕一（オフィシャルサイトの写真）'
        },
        reverse: true
      },
      {
        kind: 'twocol',
        label: 'ACTING AND SERVING',
        title: '演じることと仕えることの、近さと遠さ',
        sub: 'How acting and service differ',
        body: [],
        subs: [
          { t: '執事の仕事は演技なのか', b: '俳優としての顔を紹介すると、受講生から必ず出てくる疑問があります。執事の仕事は演技なのか、という問いです。講師の答えははっきりしています。近いのは観察の部分だけで、目的はまるで違う、というものです。' },
          { t: '近いのは、間合いの取り方', b: '近いのは、相手の呼吸や間合いを読み、自分の動きをそこに合わせる技術です。舞台でも応接でも、相手より先に動きすぎれば不自然になり、遅れれば流れが途切れます。この間合いの取り方だけは、稽古の仕方がよく似ています。' },
          { t: '遠いのは、誰のために動くか', b: '遠いのは、誰のために動くかという一点です。演技は見せるために組み立てられますが、執事の動きは見せないために削られます。見られることを前提に作る仕事と、気づかれないことを目指す仕事。同じ観察力を、正反対の方向に使う——その対比を通すと、執事の仕事の輪郭がかえってはっきりします。' },
        ],
        image: { src: '/images/related/ishii-yuichi/talkshow.jpg', alt: '登壇して語る石井裕一（石井裕一オフィシャルサイトの写真）' },
      },
      {
        kind: 'twocol',
        label: 'ON BEING SEEN',
        title: '見られる仕事の経験が、見られない仕事に返ってくる',
        sub: 'What visibility teaches about invisibility',
        body: [],
        subs: [
          { t: '見られる仕事を経験した人', b: '映画や講演のように、人前に立って記録に残る仕事を経験した人が、執事という表に出ない職業を率いている。この組み合わせ自体が、受講生にとっては考える材料になります。見られる側に立った経験があるからこそ、見られる側の負担が分かる、という説明の仕方を講師はよくします。' },
          { t: '視線を減らすこと自体がもてなし', b: '客の立場で考えれば、家の中に他人がいる状態は、それだけで見られている状態です。執事が気配を消す稽古をするのは謙遜のためではなく、相手を見られている状態から解放するためです。視線を減らすこと自体が、提供しているもてなしの中身になります。' },
          { t: '二つを併せ持つ経歴の意味', b: '人前に立つ仕事と、人の視線を減らす仕事。一人の経歴の中にその両方があることは、執事という職業が「目立たないこと」をどれだけ意識的に選んでいるかを示しています。受講生には、この対比から自分の立ち位置を考えてほしいと思っています。' },
        ],
        image: { src: '/images/related/ishii-yuichi/harvard-lecture-01.jpg', alt: '海外の大学で講演する石井裕一（石井裕一オフィシャルサイトの写真）' },
        reverse: true,
      },
      {
        kind: 'gallery',
        images: [
          { src: '/images/related/ishii-yuichi/ny-meeting.jpg', alt: '打ち合わせの場の石井裕一（オフィシャルサイトの写真）' },
          { src: '/images/related/ishii-yuichi/behind-scenes-01.jpg', alt: '『Family Romance, LLC』の撮影の合間（オフィシャルサイトの写真）' },
          { src: '/images/related/ishii-yuichi/rental-family-movie-05.jpg', alt: '『Family Romance, LLC』のスチール（縦位置・オフィシャルサイトの写真）' },
          { src: '/images/related/ishii-yuichi/fr-llc-01.jpg', alt: '『Family Romance, LLC』に本人役で出演する石井裕一（オフィシャルサイトの写真）' },
        ],
      },
      {
        kind: 'twocol',
        label: 'A SECOND LOOK',
        title: '別の顔を知ると、授業の聞こえ方が変わる',
        sub: 'The classroom sounds different afterwards',
        body: [],
        subs: [
          { t: '授業の受け取り方が変わる', b: 'このサイトを見た受講生から、授業の受け取り方が変わったという感想が届くことがあります。教室で聞く言葉が、執事の世界の内側だけで作られたものではないと分かるからです。外の世界を歩いた人が、そのうえで執事の作法を選んで残している——そう分かると、同じ話でも重さが変わります。' },
          { t: '「伝統だから」で止まらないために', b: '執事の稽古には、なぜこの形でなければならないのかを説明しにくい部分が残ります。伝統だからと言われると、受け取る側はそこで考えるのをやめてしまう。別の分野を知っている人が同じ形を選んでいるという事実は、その「なぜ」を自分で考え直すきっかけになります。' },
          { t: '外から見て、教室に戻る', b: '学ぶ側にとって大切なのは、教わったことを鵜呑みにしないことです。このサイトは、執事という職業を外側から眺め直すための窓として紹介しています。外から見てからもう一度教室に戻ってくると、同じ稽古がまったく違って見えるはずです。' },
        ],
        image: { src: '/images/related/ishii-yuichi/fr-llc-02.jpg', alt: '映画関連の一場面（石井裕一オフィシャルサイトの写真）' },
      },
      {
        kind: 'official',
        title: '石井裕一オフィシャルサイト',
        body: '俳優・実業家としての経歴と作品情報は公式サイトへ。執事としての活動は執事ベルの公式サイト、執事の学びは当サイトのコース案内をご覧ください。',
        url: 'https://getstage.com/',
        urlLabel: 'getstage.com',
      },
    ],
    faq: [
      { q: '石井裕一とはどんな人ですか。', a: '協会の頭取で、執事ベルとして執事文化を発信している人物です。株式会社ファミリーロマンスの創業者兼代表取締役でもあり、俳優としても活動しています。1981年生まれ。' },
      { q: '執事ベルのサイトとの違いは。', a: '執事ベルのサイトは執事・協会の頭取としての活動を、このサイトは俳優・実業家としての経歴をまとめています。' },
      { q: '出演作を教えてください。', a: '2019年に公開された『Family Romance, LLC』です。監督はヴェルナー・ヘルツォーク、石井は本人として主役を演じました。監督週間（カンヌ国際映画祭・第72回）に選ばれた一本です。' },
      { q: 'ハーバードでの講演とは。', a: '2019年、ハーバード大学で開かれた会議に登壇して家族代行の社会的な意味を語り、ガラス賞を受けました。会議の名称は Harvard Global Health & Leadership Conference です。' },
      { q: '本を書いていますか。', a: '『人間レンタル屋』（鉄人社、2019年）があります。代行の現場で見てきた人と人の関係をまとめた一冊です。' },
      { q: 'アカデミーとの関係は。', a: 'アカデミーの運営母体である協会の頭取です。その歩みは、執事を明確な基準で評価される専門職として社会に位置づけるというアカデミーの目標と方向を同じくしています。' },
      {
        q: '俳優としての活動と執事の活動は、どちらが本業ですか。',
        a: 'オフィシャルサイトは俳優・実業家としての経歴を、執事ベルのサイトは執事・協会の頭取としての活動を扱っています。どちらか一方が本業というより、同じ人物の二つの側面として紹介されています。'
      },
      {
        q: 'このサイトから講演を依頼できますか。',
        a: '講演や研修、メディア出演の依頼は執事ベルの公式サイトが窓口です。オフィシャルサイトは経歴と作品の紹介が中心です。'
      },
    ],
  },
];

export default sitesB;
